import React, {useState, useEffect} from "react";
import StyledTable from "../../common/StyledTable";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import DetailsModal from "../admin-concursos/DetailsModal";
import TreeView from "@material-ui/lab/TreeView";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import TreeItem from "@material-ui/lab/TreeItem";
import {makeStyles} from "@material-ui/core/styles";
import {handleLoadingConcursoError} from "../../utils/errors";
import {ActionsButtons} from "../../common/ActionButtons";

const useStyles = makeStyles({
    item: {
        "& .MuiTreeItem-iconContainer svg": {fontSize: "24px"},
        "& .MuiTreeItem-label": {fontSize: "24px"},
        "&.Mui-selected .MuiTreeItem-label": {background: "none !important"},
    },
});

type ConcursosFacultadProps = {
    visible: boolean;
    loading: boolean;
    concursosFacultad: ConcursosFacultad[];
    abierto: boolean;
};

type ConcursosFacultad = {
    nombre_facultad: string;
    data: Concurso[];
};

type Facultad = {
    id: number;
    nombre: string;
};

const ConcursosFacultad: React.FC<ConcursosFacultadProps> = (props) => {
    const classes = useStyles();
    if (props.loading) {
        return <StyledTable loading/>;
    }
    if (!props.visible) {
        return (
            <div
                style={{
                    paddingTop: "1em",
                    paddingLeft: "2em",
                    paddingRight: "2em",
                }}
            >
                <p>No hay concursos disponibles.</p>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div>
                {props.concursosFacultad.map((facultad: ConcursosFacultad, index) => {
                    return (
                        <React.Fragment key={index}>
                            <div
                                style={{
                                    paddingLeft: "2em",
                                    paddingRight: "2em",
                                }}
                            >
                                <TreeItem
                                    nodeId={facultad.nombre_facultad}
                                    className={classes.item}
                                    label={
                                        facultad.nombre_facultad
                                            ? facultad.nombre_facultad
                                            : ""
                                    }
                                >
                                    <div
                                        id={facultad.nombre_facultad + "LT"}
                                        className="mt-3"
                                    >
                                        <Concurso
                                            key={facultad.nombre_facultad}
                                            abierto={props.abierto}
                                            rows={facultad.data.map((concurso: Concurso) =>
                                                filaConcurso({
                                                    codigo: concurso.codigo,
                                                    nombre: concurso.nombre,
                                                    apertura: concurso.fecha_inicio,
                                                    cierre: concurso.fecha_termino,
                                                    concursoId: concurso.id,
                                                    numPostulantes: concurso.num_postulantes,
                                                    creador: concurso.id_usuario_creador,
                                                    contacto: concurso.id_usuario_contacto,
                                                    publicacion: concurso.fecha_aviso_diario,
                                                    abierto: props.abierto,
                                                })
                                            )}
                                        />
                                    </div>
                                </TreeItem>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </React.Fragment>
    );
};

type Concurso = {
    id: number;
    codigo: string;
    nombre: string;
    fecha_inicio: string;
    fecha_termino: string;
    fecha_aviso_diario: string;
    id_facultad: number;
    id_usuario_creador: number;
    id_usuario_contacto: number;
    num_postulantes: number;
};

type ConcursoProps = {
    abierto: boolean;
    rows: ({ content: string } | { content: JSX.Element })[][];
};

const Concurso: React.FC<ConcursoProps> = (props) => {
    const rows = props.rows;
    const headers = [
        {content: "Código"},
        {content: "Nombre"},
        ...(!props.abierto
            ? [{content: "Fecha de Publicación en el Diario"}]
            : []),
        {content: "Fecha de Apertura"},
        ...(props.abierto ? [{content: "Fecha de Cierre"}] : []),
        ...(props.abierto ? [{content: "Nº de Postulaciones"}] : [{content: "Nº de Postulaciones"}]),
        {content: "Acciones"},
    ];

    return <StyledTable headers={headers} rows={rows}/>;
};

const filaConcurso = (props: FilaConcursoProps) => {
    const fechaApertura = moment(props.apertura).locale("es").format("LLL");
    const fechaPublicacion = moment(props.publicacion).locale("es").format("LL");
    const fechaCierre = moment(props.cierre).locale("es").format("LLL");

    return [
        {content: props.codigo, style: {width: "1px"}},
        {content: props.nombre},
        ...(!props.abierto
            ? [{content: fechaPublicacion, style: {width: "254px"}}]
            : []),
        {content: fechaApertura, style: {width: "193px"}},
        ...(props.abierto
            ? [{content: fechaCierre, style: {width: "193px"}}]
            : []),
        ...(props.abierto
            ? [{content: "" + props.numPostulantes, style: {width: "172px"}}]
            : [{content: "" + props.numPostulantes, style: {width: "172px"}}]),
        ...(props.abierto
            ?
            [{content: (<ActionsButtons {...props} />), style: {width: "120px"}}]
            :
            [{
                content: (<div className="d-flex justify-content-around">
                    <DetailsModal
                        key={props.codigo + "_details"}
                        label="Detalles"
                        concursoId={props.concursoId}
                    />
                </div>), style: {width: "1px"}
            }])]

};

type FilaConcursoProps = {
    codigo: string;
    nombre: string;
    apertura: string;
    cierre: string;
    concursoId: number;
    numPostulantes: number;
    creador: number;
    contacto: number;
    publicacion: string;
    abierto: boolean;
};

type TipoAprobado = {
    abierto: boolean;
    index: number;
};

const ConcursosApproved: React.FC<TipoAprobado> = (props) => {
    const [concursos, setConcursos] = useState([]);
    const [loadingConcursos, setLoadingConcursos] = useState(true);
    const [error, setError] = useState(false);
    const [expanded, setExpanded] = React.useState([]);

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    useEffect(() => {
        /**
         function sortByFechaPublicacion(data: []) {
            let sortedData = data.sort((a: any, b: any) => new Date(b.fecha_aviso_diario).getTime() - new Date(a.fecha_aviso_diario).getTime());
            return sortedData;
        }*/

        function handleError(error: any): void {
            setError(true);
            handleLoadingConcursoError(
                error,
                (window as any).ENV.URL_FRONT + "Concursos/?tab=" + props.index
            );
        }

        let apiRoute = props.abierto
            ? "/concursosAbiertos/"
            : "/concursosAprobados/";

        axios
            .get((window as any).ENV.API_URL + apiRoute)
            .then((res) => {
                setConcursos(res.data);
                setExpanded(res.data.map((concurso) => concurso.nombre_facultad));
                setLoadingConcursos(false);
            })
            .catch((error) => handleError(error));
    }, [props.abierto]);

    if (!error)
        return (
            <TreeView
                defaultCollapseIcon={<ExpandMoreIcon/>}
                defaultExpandIcon={<ChevronRightIcon/>}
                expanded={expanded}
                onNodeToggle={handleToggle}
            >
                <ConcursosFacultad
                    loading={loadingConcursos}
                    visible={concursos.length > 0}
                    concursosFacultad={concursos}
                    abierto={props.abierto}
                />
            </TreeView>
        );
    return null;
};

export default ConcursosApproved;
